'use server'

import { User, UserCompanyAssignment } from '../common/interfaces/user.interface'
import { apiRequest, ApiResponse } from '../request'

export interface UserCompanyAssignmentInput {
    companyId: number
    roleId: number
    isDefault?: boolean
}

export async function getUsers(filters: {
    page?: number
    limit?: number
    search?: string
    status?: string
    mediatorStatus: string
    userType?: string
    userTypes?: string
    sortBy?: string
    sortOrder?: string
    incMediators?: string
}) {
    return await apiRequest<User>('users', { params: filters })
}

export async function getUsersByCompany(
    companyId: number
): Promise<ApiResponse<User[]>> {
    return apiRequest<User[]>(`users/company/${companyId}`, {})
}

/**
 * Funcionários de todas as empresas acessíveis ao usuário logado. Para telas que operam em
 * escopo de organização (ex.: relatórios com "Todas as filiais"), onde listar só a unidade
 * ativa esconderia gente que aparece nos dados exibidos.
 */
export async function getAccessibleUsers(): Promise<ApiResponse<User[]>> {
    return apiRequest<User[]>('users/accessible/list', {})
}

export interface OrganizationUser {
    id: number
    name: string
    email: string | null
    phone: string | null
    picture: string | null
    companyIds: number[]
    companyNames: string[]
}

/**
 * Todos os usuários da organização, SEM paginação. Para telas que marcam vários
 * de uma vez (membros de setor, carteiras em massa) e filtram localmente — um
 * seletor paginado esconde quem não cai na primeira página.
 */
export async function getOrganizationUsers(): Promise<
    ApiResponse<OrganizationUser[]>
> {
    return apiRequest<OrganizationUser[]>('users/organization/list', {})
}

/** Vagas de usuário de IA do plano (`features.aiUsers`; zero = o plano não inclui). */
export interface AiUserSeats {
    limit: number | 'unlimited'
    used: number
}

export async function getAiUserSeats(): Promise<ApiResponse<AiUserSeats>> {
    return apiRequest<AiUserSeats>('users/ai-user-seats', {})
}

export async function getUser(id: number) {
    return await apiRequest<User>('users/' + id, {})
}

export async function resetUserPassword(
    id: number,
    newPassword: string
): Promise<ApiResponse<any>> {
    return apiRequest<any>(`users/${id}/reset-password`, { data: { newPassword } }, 'patch')
}

export async function deactivateUserAccount(
    id: number
): Promise<ApiResponse<any>> {
    return apiRequest<any>(`users/${id}/deactivate`, {}, 'patch')
}

export async function activateUserAccount(
    id: number
): Promise<ApiResponse<any>> {
    return apiRequest<any>(`users/${id}/activate`, {}, 'patch')
}

export async function deleteUser(id: number): Promise<ApiResponse<any>> {
    return apiRequest<any>(`users/${id}`, {}, 'delete')
}

export async function blockUser(
    id: number,
    reason?: string
): Promise<ApiResponse<any>> {
    return apiRequest<any>(`users/${id}/block`, { data: { reason } }, 'patch')
}

export async function unblockUser(id: number): Promise<ApiResponse<any>> {
    return apiRequest<any>(`users/${id}/unblock`, {}, 'patch')
}

export async function createMediator(data: {
    name: string
    email: string
    password: string
    phone?: string
    pillar: string
    cpf?: string
    cnpj?: string
}): Promise<ApiResponse<User>> {
    return apiRequest<User>('users/mediators', { data }, 'post')
}

export async function updateMediator(
    id: number,
    data: {
        name: string
        phone?: string
        pillar: string
        cpf?: string
        cnpj?: string
    }
): Promise<ApiResponse<User>> {
    return apiRequest<User>(`users/mediators/${id}`, { data }, 'patch')
}

export async function createUser(data: {
    name: string
    email: string
    password: string
    phone?: string
    userType: string
    cpf?: string | null
    /** Instruções do usuário de IA (tipo AI). */
    aiInstructions?: string
    cnpj?: string
    street?: string
    state?: string
    zipCode?: string
    neighborhood?: string
    city?: string
    companies: UserCompanyAssignmentInput[]
}): Promise<ApiResponse<User>> {
    return apiRequest<User>('users/add-new', { data }, 'post')
}

export async function updateUser(
    id: number,
    data: {
        name: string
        phone?: string
        email?: string
        userType: string
        /** `null` apaga o CPF gravado; omitir mantém o valor atual. */
        cpf?: string | null
        /** Instruções do usuário de IA; `null` apaga, omitir mantém. */
        aiInstructions?: string | null
        cnpj?: string
        birthDate?: string
        street?: string
        number?: string
        complement?: string
        neighborhood?: string
        city?: string
        state?: string
        zipCode?: string
        companies?: UserCompanyAssignmentInput[]
    }
): Promise<ApiResponse<User>> {
    return apiRequest<User>(`users/edit/${id}`, { data }, 'patch')
}

export async function changeUserPassword(
    id: number,
    data: {
        currentPassword: string
        newPassword: string
        terminateSessions?: boolean
    }
): Promise<ApiResponse<{ message: string; sessionsTerminated?: boolean }>> {
    return apiRequest<{ message: string; sessionsTerminated?: boolean }>(
        `users/${id}/change-password`,
        {
            data,
        },
        'patch'
    )
}

/**
 * Foto do próprio usuário. O id vem do token no backend, não da rota — não há
 * como trocar a foto de outra pessoa, e por isso o endpoint não exige a permissão
 * de editar funcionários.
 */
export async function uploadOwnPicture(
    file: File
): Promise<ApiResponse<{ picture: string | null }>> {
    const formData = new FormData()
    formData.append('picture', file)
    return apiRequest<{ picture: string | null }>(
        'users/me/picture',
        { data: formData, formData: true },
        'post'
    )
}

/**
 * Comissão por produtividade do usuário — o que ele recebe pelos atendimentos que
 * AGENDA (a aba "Produtividade" dos relatórios apura por quem criou o registro).
 *
 * Mora no usuário, e não na ficha de RH, porque a apuração é por usuário do sistema e
 * nem todo mundo que agenda tem ficha (o RH não tem sequer contrato de PJ). A taxa era
 * digitada a cada apuração, sem registro do que havia sido combinado.
 */
export type UserCommissionMode = 'FIXED' | 'PERCENTAGE'

export interface UserCommission {
    id: number
    userId: number
    /** Modo GERAL, usado pelos tipos sem modo próprio. */
    mode: UserCommissionMode
    /**
     * Taxa geral. Em `FIXED` são centavos por atendimento; em `PERCENTAGE`, basis
     * points (1250 = 12,5%). `null` quando só há taxas por tipo.
     */
    rate: number | null
    /** Taxas por tipo. `null` = usa a geral. */
    rateConsultation: number | null
    rateExam: number | null
    rateOrder: number | null
    /**
     * Critério próprio do tipo. `null` = segue o geral.
     *
     * Preenchido, a taxa geral **não** é herdada — "12,5" percentual não vira R$ 0,12
     * fixo —, então a taxa daquele tipo passa a ser obrigatória.
     */
    modeConsultation: UserCommissionMode | null
    modeExam: UserCommissionMode | null
    modeOrder: UserCommissionMode | null
    updatedBy?: { id: number; name: string } | null
    updatedAt?: string | null
}

export interface UpsertUserCommissionInput {
    mode: UserCommissionMode
    rate?: number | null
    rateConsultation?: number | null
    rateExam?: number | null
    rateOrder?: number | null
    modeConsultation?: UserCommissionMode | null
    modeExam?: UserCommissionMode | null
    modeOrder?: UserCommissionMode | null
}

export async function getUserCommission(
    userId: number
): Promise<ApiResponse<UserCommission | null>> {
    return apiRequest<UserCommission | null>(`users/${userId}/commission`, {})
}

/** Gravar exige permissão de RH ou de financeiro — o backend recusa os demais. */
export async function upsertUserCommission(
    userId: number,
    data: UpsertUserCommissionInput
): Promise<ApiResponse<UserCommission>> {
    return apiRequest<UserCommission>(
        `users/${userId}/commission`,
        { data },
        'put'
    )
}

export async function removeUserCommission(
    userId: number
): Promise<ApiResponse<{ removed: boolean }>> {
    return apiRequest<{ removed: boolean }>(
        `users/${userId}/commission`,
        {},
        'delete'
    )
}
